import { PhoneSyncApi, useQueryDevices } from '@frontend/api-phone-sync';
import { useTranslation } from '@frontend/i18n';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import {
  DeviceSelectModal,
  PhoneSyncButton,
  PhoneSyncModal,
  usePhonePairSuccessHandler,
  usePhoneSyncControl,
} from '@frontend/phone-sync';
import { useAppScopeStore } from '@frontend/scope';
import { Modal, ModalControlResponse, SpinningLoader } from '@frontend/design-system';
import { useDialpadTrigger } from '../use-call-action';
import { NoDevicePairedModal } from './no-device-paired';

type ClickToCallSelectionProps = {
  modal: ModalControlResponse;
  context: {
    dialNumber: string;
    isLoading: boolean;
    outboundNumber?: string;
  };
};

export const ClickToCallSelection = ({ modal, context }: ClickToCallSelectionProps) => {
  const { t } = useTranslation('phone-call');
  const { selectedLocationIds } = useAppScopeStore();
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');

  const { modalProps, buttonProps } = usePhoneSyncControl();
  const { data: devices } = useQueryDevices(selectedLocationIds);

  const availableDevices = devices?.filter((device) => device.availableInContext) ?? [];
  const onSyncSuccess = usePhonePairSuccessHandler()(selectedLocationIds);
  const { triggerDialpad } = useDialpadTrigger();

  const { dialNumber, isLoading, outboundNumber } = context;

  return (
    <div>
      {isLoading ? (
        <Modal {...modal.modalProps} maxWidth={400}>
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SpinningLoader />
          </div>
        </Modal>
      ) : (
        <>
          {!phoneConfig && availableDevices && availableDevices.length > 0 && (
            <>
              <DeviceSelectModal
                variant='click-to-call'
                deviceSelectModal={modal}
                closeModal={modal.closeModal}
                locationIds={selectedLocationIds}
                pairDeviceButton={
                  <PhoneSyncButton {...buttonProps} variant='secondary' onClick={modal.modalProps.onClose} />
                }
                onConnectSuccess={() => {
                  triggerDialpad(dialNumber, outboundNumber);
                }}
              />
              <PhoneSyncModal
                {...modalProps}
                locationIds={selectedLocationIds}
                onSyncSuccess={onSyncSuccess}
                syncFn={PhoneSyncApi.pairDevice}
              />
            </>
          )}

          {!phoneConfig && availableDevices?.length === 0 && (
            <>
              <NoDevicePairedModal
                modalProps={modal.modalProps}
                pairDeviceButton={
                  <PhoneSyncButton {...buttonProps} btnLabel={t('Pair Phone')} onClick={modal.modalProps.onClose} />
                }
              />
              <PhoneSyncModal
                {...modalProps}
                locationIds={selectedLocationIds}
                onSyncSuccess={onSyncSuccess}
                syncFn={PhoneSyncApi.pairDevice}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};
